import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import type { ResponsiveStyleValue } from "@mui/system";
import type { FC, KeyboardEvent, RefObject } from "react";
import { CONTROL_HEIGHT_PX, FONT_SIZE, ICON_SIZE, INK, NEUTRAL } from "../../../theme/tokens";
import { FILTER_SEARCH_CANCEL_BUTTON_SX, filterSearchHtmlInputProps } from "./filterSearchFieldProps";
import { SystemValidationTextField } from "./SystemValidationTextField";

/**
 * How big the box is and how it sits in its surroundings.
 *
 * - `toolbar` — MUI's small outlined field on the page's own surface, with a `fontSize="small"` glyph: a grid's or a
 *   page's toolbar search, a dialog's list filter.
 * - `sheet` — touch-sized for a bottom sheet pinned over the list it narrows: `CONTROL_HEIGHT.STANDARD`, `EMPHASIS`
 *   type, a quiet `NEUTRAL[50]` fill so it reads as part of the heading rather than a form field, and a muted glyph at
 *   the sheet rows' `ICON_SIZE.MENU_ROW`.
 * - `compact` — dense pinned chrome that has to leave the list below it its room: `CONTROL_HEIGHT.COMPACT`,
 *   `SECONDARY` type, the same quiet fill, a smaller muted glyph.
 * - `filterBar` — the `toolbar` box at `EMPHASIS` type, for a page's filter bar whose chips and dropdowns are set at
 *   14px (the Locations toolbar), so the search doesn't read a size larger than the controls beside it.
 */
export type SystemSearchFieldVariant = "toolbar" | "sheet" | "compact" | "filterBar";

/**
 * The compact variant's glyph. No `ICON_SIZE` step is 16px (`PILL` is 14, `INLINE`/`MENU_ROW` 18), and the mobile Gaps
 * row has always drawn it at 16 — kept as is rather than moved to a neighbouring step, which would change that row.
 */
const COMPACT_GLYPH_PX = 16;

/** The box's own look per variant — layout (width, flex) stays the caller's. */
const VARIANT_INPUT_SX = {
	toolbar: FILTER_SEARCH_CANCEL_BUTTON_SX,
	sheet: {
		...FILTER_SEARCH_CANCEL_BUTTON_SX,
		"& .MuiOutlinedInput-root": { height: CONTROL_HEIGHT_PX.STANDARD, fontSize: FONT_SIZE.EMPHASIS, bgcolor: NEUTRAL[50] },
	},
	compact: {
		...FILTER_SEARCH_CANCEL_BUTTON_SX,
		"& .MuiOutlinedInput-root": { height: CONTROL_HEIGHT_PX.COMPACT, fontSize: FONT_SIZE.SECONDARY, bgcolor: NEUTRAL[50] },
	},
	filterBar: {
		...FILTER_SEARCH_CANCEL_BUTTON_SX,
		"& .MuiOutlinedInput-root": { fontSize: FONT_SIZE.EMPHASIS },
	},
} as const;

const VARIANT_GLYPH = {
	toolbar: <SearchIcon fontSize="small" />,
	sheet: <SearchIcon sx={{ fontSize: ICON_SIZE.MENU_ROW, color: INK.MUTED }} />,
	compact: <SearchIcon sx={{ fontSize: COMPACT_GLYPH_PX, color: INK.MUTED }} />,
	filterBar: <SearchIcon fontSize="small" />,
} as const;

/** A flexible width: as wide as the row allows, between these widths (px). */
type SearchWidthRange = { min: number; max: number };

const isWidthRange = (width: Props["width"]): width is SearchWidthRange => typeof width === "object" && width !== null && "min" in width && "max" in width;

type Props = {
	value: string;
	/** Every keystroke, and `""` when the field is cleared (its clear button, or Escape with `clearOnEscape`). */
	onChange: (value: string) => void;
	placeholder?: string;
	/** A stable, non-identity-shaped input name (e.g. "grid-search"): it keeps password managers off the box. */
	name: string;
	/** DEFAULT: `toolbar` — the grid and page toolbars the component was extracted from, and most searches in the app. */
	variant?: SystemSearchFieldVariant;
	/**
	 * The field's width; responsive values let it shrink or take a full line on a narrow screen. A `{min, max}` range
	 * (px) makes it flex instead: it grows into its row's spare room up to `max` and shrinks to `min` before the row wraps.
	 */
	width?: ResponsiveStyleValue<number | string> | SearchWidthRange;
	/** A cap on `width`, e.g. `"100%"` so a fixed-width field still fits a narrow container. Absent = no cap. */
	maxWidth?: ResponsiveStyleValue<number | string>;
	/** Fills its container's width (MUI `fullWidth`). Absent = sized by `width`, or by its content. */
	fullWidth?: boolean;
	/** Takes the room left in a flex row and may shrink below its content (`flex: 1; min-width: 0`). Absent = no flex. */
	grow?: boolean;
	disabled?: boolean;
	autoFocus?: boolean;
	/**
	 * Escape clears the term, and `preventDefault`s so the same key isn't also read by the page (e.g. as "clear the
	 * selection"). With nothing to clear the key is left alone, so the page still gets it.
	 * DEFAULT: off — most of these fields sit in a dialog or popover where Escape already means "close".
	 */
	clearOnEscape?: boolean;
	/** Lets a page put the caret in the field (e.g. a column's "Filter by" handing off to the page search). */
	inputRef?: RefObject<HTMLInputElement | null>;
	/**
	 * The input's accessible name, for a field whose placeholder shouldn't be its only name. Absent = no
	 * `aria-label`, as the grid's own search has always rendered.
	 */
	ariaLabel?: string;
	dataTestId?: string;
};

/**
 * The app's list-filtering search box: a search-typed `SystemValidationTextField` with the magnifier at the start, a
 * clear button once there is something to clear, and the attributes that keep password managers from treating it as a
 * login field (`filterSearchFieldProps`). `SystemDataGrid` draws its toolbar search with it; use it for any search that
 * filters a list — a page toolbar, a dialog's option list, a bottom sheet's pick list (`variant`).
 */
export const SystemSearchField: FC<Props> = ({
	value,
	onChange,
	placeholder,
	name,
	// DEFAULT: see the prop doc — the toolbar look is the one every pre-variant caller rendered.
	variant = "toolbar",
	width,
	maxWidth,
	fullWidth,
	grow,
	disabled,
	autoFocus,
	clearOnEscape,
	inputRef,
	ariaLabel,
	dataTestId,
}) => {
	const htmlInput = ariaLabel === undefined ? filterSearchHtmlInputProps(name) : { ...filterSearchHtmlInputProps(name), "aria-label": ariaLabel };
	return (
		<SystemValidationTextField
			value={value}
			onChange={(event) => onChange(event.target.value)}
			placeholder={placeholder}
			reserveHeightForHelperText={false}
			// The text field draws its clear button only while there is a value, so this is "once there is text".
			onClear={() => onChange("")}
			onKeyDown={
				clearOnEscape
					? (event: KeyboardEvent<HTMLDivElement>) => {
							if (event.key !== "Escape" || value === "") return;
							event.preventDefault();
							onChange("");
						}
					: undefined
			}
			disabled={disabled}
			autoFocus={autoFocus}
			inputType="search"
			inputSx={VARIANT_INPUT_SX[variant]}
			injectedProps={inputRef === undefined ? { slotProps: { htmlInput } } : { inputRef, slotProps: { htmlInput } }}
			inputProps={{ startAdornment: <InputAdornment position="start">{VARIANT_GLYPH[variant]}</InputAdornment> }}
			fullWidth={fullWidth}
			formControlSx={
				isWidthRange(width)
					? { flex: `1 1 ${width.min}px`, minWidth: width.min, maxWidth: width.max }
					: { width, maxWidth, ...(grow && { flex: 1, minWidth: 0 }) }
			}
			dataTestId={dataTestId}
		/>
	);
};
